/**
 * EinstellungenPage – persönliche Einstellungen (jeder eingeloggte User).
 *
 *  - ToDo-Quelle für die Leiste auf der Homepage (eigene Liste / Jira / Trello)
 *  - Eigene Jira-Verbindung (holthey.atlassian.net): E-Mail + API-Token.
 *    Das Token ist write-only — es wird verschlüsselt gespeichert und NIE
 *    wieder angezeigt (auch nicht für Admins).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";
import { wendeDarstellungAn } from "../utils/darstellung";
import "./EinstellungenPage.css";

const api = (path, opts = {}) =>
  fetch(`${API_BASE}/benutzer${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-MA-Token": localStorage.getItem("maToken") || "",
      ...(opts.headers || {}),
    },
  });

export default function EinstellungenPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [laden, setLaden] = useState(true);
  const [einst, setEinst] = useState({
    todo_quelle: "eigene", jira_email: "", jira_jql: "", jira_verbunden: false,
    theme: "dunkel", schriftgroesse: "normal",
  });
  const [tokenNeu, setTokenNeu] = useState("");
  const [jqlOffen, setJqlOffen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meldung, setMeldung] = useState(null); // {ok, text}
  // API-Schlüssel (Welle 62): feststehende Tokens für Excel/Skripte
  const [apiKeys, setApiKeys] = useState([]);
  const [keyLabel, setKeyLabel] = useState("");
  const [copiedKey, setCopiedKey] = useState(null); // id des zuletzt kopierten

  const lade = useCallback(async () => {
    setLaden(true);
    try {
      const r = await api("/einstellungen");
      if (r.ok) {
        const d = await r.json();
        setEinst(d);
        wendeDarstellungAn(d);
      }
    } catch (_) {}
    try {
      const rk = await api("/api-schluessel");
      if (rk.ok) setApiKeys((await rk.json()).schluessel || []);
    } catch (_) {}
    setLaden(false);
  }, []);

  useEffect(() => { lade(); }, [lade]);

  const zeige = (ok, text) => {
    setMeldung({ ok, text });
    setTimeout(() => setMeldung(null), 5000);
  };

  const speichern = async (extra = {}) => {
    setSaving(true);
    try {
      const body = {
        todo_quelle: einst.todo_quelle,
        jira_email: einst.jira_email,
        jira_jql: einst.jira_jql,
        theme: einst.theme,
        schriftgroesse: einst.schriftgroesse,
        ...(tokenNeu ? { jira_token: tokenNeu } : {}),
        ...extra,
      };
      const r = await api("/einstellungen", { method: "PUT", body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Speichern fehlgeschlagen");
      setTokenNeu("");
      zeige(true, "Gespeichert.");
      lade();
    } catch (e) {
      zeige(false, String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const testen = async () => {
    setSaving(true);
    try {
      // Ungespeichertes Token vorher mitspeichern, damit der Test das Richtige prüft
      if (tokenNeu) {
        const r0 = await api("/einstellungen", {
          method: "PUT",
          body: JSON.stringify({ jira_email: einst.jira_email, jira_token: tokenNeu }),
        });
        if (!r0.ok) throw new Error("Speichern vor dem Test fehlgeschlagen");
        setTokenNeu("");
      }
      const r = await api("/jira/test", { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Test fehlgeschlagen");
      zeige(true, `✓ Verbunden als ${d.displayName || d.email}`);
      lade();
    } catch (e) {
      zeige(false, String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const trennen = () => speichern({ jira_token: "" });

  // ── Darstellung (Welle 99) ────────────────────────────────────────────────
  // Sofort anwenden, damit man die Wirkung sieht, und gleich mitspeichern —
  // ein extra „Speichern" für eine Sichtsache wäre eine Falle.
  const darstellungSetzen = (feld, wert) => {
    const neu = { ...einst, [feld]: wert };
    setEinst(neu);
    wendeDarstellungAn(neu);
    speichern({ [feld]: wert });
  };

  // ── API-Schlüssel (Welle 62) ──────────────────────────────────────────────
  const keyErzeugen = async () => {
    setSaving(true);
    try {
      const r = await api("/api-schluessel", { method: "POST", body: JSON.stringify({ label: keyLabel }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Erzeugen fehlgeschlagen");
      setKeyLabel("");
      zeige(true, "Schlüssel erzeugt.");
      lade();
    } catch (e) {
      zeige(false, String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const keyWiderrufen = async (id) => {
    if (!window.confirm("Diesen Schlüssel widerrufen? Alle Programme, die ihn nutzen, verlieren sofort den Zugriff.")) return;
    setSaving(true);
    try {
      const r = await api(`/api-schluessel/${id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Widerrufen fehlgeschlagen");
      zeige(true, "Schlüssel widerrufen.");
      lade();
    } catch (e) {
      zeige(false, String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const keyKopieren = async (k, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(k);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      window.prompt("Diesen Wert kopieren:", text);
    }
  };

  if (laden) return <div className="es-page"><div className="es-laden">Lädt…</div></div>;

  return (
    <div className="es-page">
      <div className="es-header">
        <button className="es-back" onClick={() => navigate("/")}>← Home</button>
        <h1>👤 Meine Einstellungen</h1>
        <span className="es-user">{user?.kuerzel}</span>
      </div>

      {meldung && (
        <div className={`es-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>
      )}

      <div className="es-card">
        <h2>Darstellung</h2>
        <p className="es-hint">Gilt nur für dich. Die Änderung ist sofort sichtbar.</p>
        <label className="es-label">Hintergrund</label>
        <div className="es-radios">
          {[["dunkel", "🌙 Dunkel"], ["hell", "☀️ Hell"]].map(([w, l]) => (
            <label key={w} className="es-radio">
              <input type="radio" name="theme" checked={einst.theme === w}
                onChange={() => darstellungSetzen("theme", w)} />
              <span>{l}</span>
            </label>
          ))}
        </div>
        <label className="es-label">Schriftgröße</label>
        <div className="es-radios">
          {[["normal", "Normal"], ["gross", "Groß"], ["sehr_gross", "Sehr groß"]].map(([w, l]) => (
            <label key={w} className="es-radio">
              <input type="radio" name="schriftgroesse" checked={einst.schriftgroesse === w}
                onChange={() => darstellungSetzen("schriftgroesse", w)} />
              <span>{l}</span>
            </label>
          ))}
        </div>
        {einst.theme === "hell" && (
          <p className="es-hint">
            Hell ist bisher für Startseite, Navigationsleiste, Posteingang, Verschieben,
            Bilder/Fotos, Rechnungen und Postkorb umgesetzt. Andere Seiten bleiben vorerst dunkel.
          </p>
        )}
      </div>

      <div className="es-card">
        <h2>ToDo-Leiste auf der Startseite</h2>
        <p className="es-hint">Was soll rechts in deiner ToDo-Liste angezeigt werden?</p>
        <div className="es-radios">
          {[["eigene", "📝 Eigene Liste"], ["jira", "🎫 Meine Jira-Vorgänge"], ["trello", "📋 Trello-Karten"]].map(([w, l]) => (
            <label key={w} className="es-radio">
              <input type="radio" checked={einst.todo_quelle === w}
                onChange={() => setEinst((e) => ({ ...e, todo_quelle: w }))} />
              <span>{l}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="es-card">
        <h2>Jira-Verbindung <span className="es-hint">(holthey.atlassian.net)</span></h2>
        {einst.jira_verbunden ? (
          <div className="es-status ok">✓ Verbunden als <b>{einst.jira_email || "—"}</b></div>
        ) : (
          <div className="es-status">Noch keine Verbindung hinterlegt.</div>
        )}
        <label className="es-label">Jira-E-Mail
          <input className="es-input" value={einst.jira_email} autoCapitalize="none"
            placeholder="name@firma.de"
            onChange={(e) => setEinst((x) => ({ ...x, jira_email: e.target.value }))} />
        </label>
        <label className="es-label">API-Token {einst.jira_verbunden && <span className="es-hint">(leer lassen = unverändert)</span>}
          <input className="es-input" type="password" value={tokenNeu}
            placeholder="Jira-API-Token einfügen…"
            onChange={(e) => setTokenNeu(e.target.value)} />
        </label>
        <p className="es-hint">
          Token erstellen: Jira → Profil → Sicherheit → API-Token verwalten.
          Dein Token wird verschlüsselt gespeichert und niemandem angezeigt — auch keinem Admin.
        </p>
        <button className="es-link" onClick={() => setJqlOffen(!jqlOffen)}>
          {jqlOffen ? "▲" : "▼"} Erweitert: eigene JQL-Abfrage
        </button>
        {jqlOffen && (
          <label className="es-label">JQL (leer = offene Vorgänge, die mir zugewiesen sind)
            <input className="es-input" value={einst.jira_jql}
              placeholder='assignee = currentUser() AND statusCategory != Done'
              onChange={(e) => setEinst((x) => ({ ...x, jira_jql: e.target.value }))} />
          </label>
        )}
        <div className="es-buttons">
          <button className="es-btn primary" disabled={saving} onClick={() => speichern()}>
            {saving ? "Speichert…" : "Speichern"}
          </button>
          <button className="es-btn" disabled={saving} onClick={testen}>Verbindung testen</button>
          {einst.jira_verbunden && (
            <button className="es-btn danger" disabled={saving} onClick={trennen}>Verbindung trennen</button>
          )}
        </div>
      </div>

      <div className="es-card">
        <h2>API-Schlüssel <span className="es-hint">(für Excel, Power Query & Skripte)</span></h2>
        <p className="es-hint">
          Ein feststehender Schlüssel mit deinen Rechten — läuft nie ab, keine Anmeldung nötig.
          Einfach kopieren und in Excel/Skripte einsetzen. Wie ein Passwort behandeln —
          bei Verlust hier widerrufen und neu erzeugen.
        </p>
        {apiKeys.length === 0 && (
          <div className="es-status">Noch kein Schlüssel vorhanden.</div>
        )}
        {apiKeys.map((k) => (
          <div key={k.id} className="es-key-row">
            <div className="es-key-top">
              <b>{k.label}</b>
              <span className="es-hint">
                {k.zuletzt_benutzt ? `zuletzt benutzt: ${String(k.zuletzt_benutzt).slice(0, 16).replace("T", " ")}` : "noch nie benutzt"}
              </span>
              <button className="es-btn danger small" disabled={saving} onClick={() => keyWiderrufen(k.id)}>Widerrufen</button>
            </div>
            <div className="es-key-line">
              <code className="es-key-code">{k.schluessel}</code>
              <button className="es-btn small" onClick={() => keyKopieren(k.id, k.schluessel)}>
                {copiedKey === k.id ? "✓ kopiert" : "Kopieren"}
              </button>
            </div>
            <div className="es-key-line">
              <span className="es-hint">Power-Query-URL (Tabelle „daten"):</span>
              <code className="es-key-code klein">{`${API_BASE}/database/table/daten/json?token=${k.schluessel}`}</code>
              <button className="es-btn small"
                onClick={() => keyKopieren(`url-${k.id}`, `${API_BASE}/database/table/daten/json?token=${k.schluessel}`)}>
                {copiedKey === `url-${k.id}` ? "✓ kopiert" : "URL kopieren"}
              </button>
            </div>
            <p className="es-hint">
              Funktioniert überall: URL um <code>?token=…</code> ergänzen oder Header <code>X-MA-Token</code> setzen.
              Fertigen Excel-Code je Tabelle gibt es auf der DB-Tabellen-Seite über den Excel-Knopf.
            </p>
          </div>
        ))}
        <div className="es-buttons">
          <input className="es-input klein" value={keyLabel} placeholder="Bezeichnung (optional, z. B. Excel)"
            onChange={(e) => setKeyLabel(e.target.value)} />
          <button className="es-btn primary" disabled={saving} onClick={keyErzeugen}>
            {saving ? "Bitte warten…" : "Neuen Schlüssel erzeugen"}
          </button>
        </div>
      </div>
    </div>
  );
}
